import { DragEvent, FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useMutation } from "convex/react";
import { parseDate as parseCalendarDate } from "@internationalized/date";
import { Button } from "react-aria-components";
import type { DateValue } from "react-aria-components";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { api, Id } from "@/smart-views/client/api";
import { ChippyText, hasChips } from "./chips";
import {
  MDSActionMenu,
  MDSActionMenuItem,
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
  MDSCalendar,
  MDSIcon,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSMenuDropdown,
  MDSMenuDropdownSlotName,
  MDSMenuItemVariant,
} from "@/mds";
import { ZIndex } from "@/domains/design/constants";
import { CitationChip } from "../citations/CitationChip";
import { getVisibleSourceIdsForRepeatedRun } from "../citations/source-utils";

type TaskPriority = "low" | "medium" | "high" | "urgent";

interface WorkflowSection {
  id: string;
  title: string;
}

interface Task {
  id: string;
  label: string;
  done: boolean;
  sectionId?: string;
  dueDate?: string;
  priority?: TaskPriority;
  hidden?: boolean;
  sources?: string[];
}

interface TaskGroup {
  section: WorkflowSection | null;
  tasks: Task[];
  showHeader: boolean;
}

type DropPlacement = "before" | "after";

const TASK_HOVER_CONTROL_CLASS = "task-hover-control";

interface TaskListBlockProps {
  blockId: Id<"blocks">;
  tasks: Task[];
  sections?: WorkflowSection[];
  isReadOnly?: boolean;
  onUserAction?: (summary: string) => void;
}

export const TaskListBlock: FC<TaskListBlockProps> = ({
  blockId,
  tasks,
  sections = [],
  isReadOnly = false,
  onUserAction,
}) => {
  const updateContent = useMutation(api.smartViews.client.updateBlockContent);
  const [draft, setDraft] = useState<Task[]>(tasks);
  const [draftSections, setDraftSections] = useState<WorkflowSection[]>(sections);
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dropTargetTask, setDropTargetTask] = useState<{
    taskId: string;
    placement: DropPlacement;
  } | null>(null);

  useEffect(() => {
    setDraft(tasks);
    setDraftSections(sections);
  }, [tasks, sections]);

  const commit = (nextTasks: Task[] = draft, nextSections: WorkflowSection[] = draftSections) => {
    void updateContent({
      blockId,
      content: buildTaskListContent(nextTasks, nextSections),
    });
  };

  const addTask = (sectionId?: string) => {
    const next = [
      ...draft,
      {
        id: crypto.randomUUID(),
        label: "",
        done: false,
        ...(sectionId ? { sectionId } : {}),
      },
    ];
    setDraft(next);
    commit(next);
  };

  const addSection = () => {
    const nextSections = [
      ...draftSections,
      {
        id: crypto.randomUUID(),
        title: `Section ${draftSections.length + 1}`,
      },
    ];
    setDraftSections(nextSections);
    commit(draft, nextSections);
  };

  const updateSectionTitle = (id: string, title: string, shouldCommit = false) => {
    const nextSections = draftSections.map(section =>
      section.id === id ? { ...section, title } : section
    );
    setDraftSections(nextSections);
    if (shouldCommit) {
      commit(draft, nextSections);
    }
  };

  const updateTask = (id: string, patch: Partial<Task>, shouldCommit = false) => {
    const previous = draft.find(t => t.id === id);
    const next = draft.map(t => (t.id === id ? { ...t, ...patch } : t));
    setDraft(next);
    if (shouldCommit || patch.done !== undefined) {
      commit(next);
    }
    if (patch.done !== undefined) {
      if (previous && previous.done !== patch.done && onUserAction) {
        const label = previous.label.trim() || "(untitled task)";
        onUserAction(patch.done ? `Marked "${label}" as done.` : `Reopened "${label}".`);
      }
    }
  };

  const removeTask = (id: string) => {
    const next = draft.filter(t => t.id !== id);
    setDraft(next);
    commit(next);
  };

  const hideTask = (id: string) => {
    updateTask(id, { hidden: true }, true);
  };

  const moveTask = (sourceTaskId: string, targetTaskId: string, placement: DropPlacement) => {
    if (sourceTaskId === targetTaskId) return;

    const sourceIndex = draft.findIndex(task => task.id === sourceTaskId);
    const targetTask = draft.find(task => task.id === targetTaskId);
    if (sourceIndex === -1 || !targetTask) return;

    const movingTask = {
      ...draft[sourceIndex],
      sectionId: targetTask.sectionId,
    };
    const remaining = draft.filter(task => task.id !== sourceTaskId);
    const targetIndex = remaining.findIndex(task => task.id === targetTaskId);
    if (targetIndex === -1) return;

    const insertIndex = placement === "after" ? targetIndex + 1 : targetIndex;
    const next = [...remaining];
    next.splice(insertIndex, 0, movingTask);
    setDraft(next);
    commit(next);
  };

  const groups = buildTaskGroups({
    tasks: draft,
    sections: draftSections,
  });

  return (
    <List>
      {groups.map(group => (
        <SectionGroup key={group.section?.id ?? "unsectioned"}>
          {group.showHeader && (
            <SectionHeader>
              {group.section ? (
                <SectionTitleInput
                  value={group.section.title}
                  readOnly={isReadOnly}
                  placeholder="Section"
                  onChange={e => updateSectionTitle(group.section!.id, e.target.value)}
                  onBlur={() => commit()}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.currentTarget.blur();
                    }
                  }}
                />
              ) : (
                <SectionTitle>Other</SectionTitle>
              )}
            </SectionHeader>
          )}
          {group.tasks.map((task, idx) => {
            const visibleSourceIds = getVisibleSourceIdsForRepeatedRun({
              sourceIds: task.sources,
              previousSourceIds: group.tasks[idx - 1]?.sources,
            });

            return (
              <Row
                key={task.id}
                $dropPlacement={
                  dropTargetTask?.taskId === task.id ? dropTargetTask.placement : null
                }
                onDragOver={event => {
                  if (isReadOnly || !draggedTaskId || draggedTaskId === task.id) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                  setDropTargetTask({
                    taskId: task.id,
                    placement: getDropPlacement(event),
                  });
                }}
                onDragLeave={() => setDropTargetTask(null)}
                onDrop={event => {
                  event.preventDefault();
                  if (!draggedTaskId) return;
                  const placement =
                    dropTargetTask?.taskId === task.id
                      ? dropTargetTask.placement
                      : getDropPlacement(event);
                  moveTask(draggedTaskId, task.id, placement);
                  setDraggedTaskId(null);
                  setDropTargetTask(null);
                }}
              >
                <Checkbox
                  type="checkbox"
                  checked={task.done}
                  disabled={isReadOnly}
                  onChange={e => updateTask(task.id, { done: e.target.checked })}
                />
                <TaskLabelInput
                  task={task}
                  isReadOnly={isReadOnly}
                  onChange={label => updateTask(task.id, { label })}
                  onBlur={() => commit()}
                  onBackspaceEmpty={() => removeTask(task.id)}
                  onEnter={() => addTask(task.sectionId)}
                />
                <TaskMetadata
                  className={!task.priority && !task.dueDate ? TASK_HOVER_CONTROL_CLASS : undefined}
                >
                  {isReadOnly ? (
                    <>
                      {task.priority && (
                        <PriorityBadge $priority={task.priority}>
                          {TASK_PRIORITY_LABELS[task.priority]}
                        </PriorityBadge>
                      )}
                      {task.dueDate && <DueDateBadge>{formatDueDate(task.dueDate)}</DueDateBadge>}
                    </>
                  ) : (
                    <>
                      <TaskPriorityControl
                        priority={task.priority}
                        onChange={priority =>
                          updateTask(
                            task.id,
                            {
                              priority,
                            },
                            true
                          )
                        }
                      />
                      <TaskDueDateControl
                        dueDate={task.dueDate}
                        onChange={dueDate =>
                          updateTask(
                            task.id,
                            {
                              dueDate,
                            },
                            true
                          )
                        }
                      />
                    </>
                  )}
                </TaskMetadata>
                {visibleSourceIds.length > 0 && (
                  <InlineSources>
                    {visibleSourceIds.map(noteId => (
                      <CitationChip key={noteId} noteId={noteId} />
                    ))}
                  </InlineSources>
                )}
                {!isReadOnly && (
                  <TaskRowControls>
                    <TaskDragHandle
                      draggable
                      aria-label="Reorder task"
                      onDragStart={event => {
                        setDraggedTaskId(task.id);
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", task.id);
                      }}
                      onDragEnd={() => {
                        setDraggedTaskId(null);
                        setDropTargetTask(null);
                      }}
                    >
                      <MDSIcon kind={MDSIconKind.Grip} />
                    </TaskDragHandle>
                    <TaskRowMenu
                      hasPriority={Boolean(task.priority)}
                      hasDueDate={Boolean(task.dueDate)}
                      onClearPriority={() => updateTask(task.id, { priority: undefined }, true)}
                      onClearDueDate={() => updateTask(task.id, { dueDate: undefined }, true)}
                      onHide={() => hideTask(task.id)}
                    />
                  </TaskRowControls>
                )}
              </Row>
            );
          })}
          {!isReadOnly && draftSections.length > 0 && (
            <AddRow onClick={() => addTask(group.section?.id)}>+ Add task</AddRow>
          )}
        </SectionGroup>
      ))}
      {!isReadOnly && (
        <FooterActions>
          <AddRow onClick={() => addTask()}>+ Add task</AddRow>
          <AddRow onClick={addSection}>+ Add section</AddRow>
        </FooterActions>
      )}
    </List>
  );
};

const TaskPriorityControl: FC<{
  priority?: TaskPriority;
  onChange: (priority: TaskPriority | undefined) => void;
}> = ({ priority, onChange }) => {
  const [isPriorityMenuOpen, setIsPriorityMenuOpen] = useState(false);

  const selectPriority = (nextPriority: TaskPriority | undefined) => {
    onChange(nextPriority);
    setIsPriorityMenuOpen(false);
  };

  const priorityMenu = useTaskFloatingPopover({
    isOpen: isPriorityMenuOpen,
    onOpenChange: setIsPriorityMenuOpen,
    children: (
      <MDSActionMenu aria-label="Task priority" style={{ minWidth: 160 }}>
        {TASK_PRIORITY_OPTIONS.map(option => (
          <MDSActionMenuItem
            key={option}
            id={option}
            variant={MDSMenuItemVariant.OneLine}
            label={TASK_PRIORITY_LABELS[option]}
            icon={priority === option ? MDSIconKind.Check : MDSIconKind.Circle}
            onClick={() => selectPriority(option)}
          />
        ))}
        {priority && (
          <MDSActionMenuItem
            id="clear-priority"
            variant={MDSMenuItemVariant.OneLine}
            label="Clear priority"
            icon={MDSIconKind.Clear}
            onClick={() => selectPriority(undefined)}
          />
        )}
      </MDSActionMenu>
    ),
  });

  if (!priority) {
    return (
      <ControlFrame
        className={TASK_HOVER_CONTROL_CLASS}
        data-menu-open={isPriorityMenuOpen ? "true" : undefined}
      >
        <MDSIconButton
          ref={priorityMenu.setReference}
          size={MDSIconButtonSize.XSmall}
          iconKind={MDSIconKind.Flag}
          variant={MDSIconButtonVariant.OnChrome}
          aria-label="Set priority"
          onPress={() => setIsPriorityMenuOpen(isOpen => !isOpen)}
          rounded
        />
        {priorityMenu.content}
      </ControlFrame>
    );
  }

  return (
    <ControlFrame>
      <MetadataButton
        ref={priorityMenu.setReference}
        type="button"
        $tone={priority}
        aria-label={`Priority: ${TASK_PRIORITY_LABELS[priority]}`}
        onPress={() => setIsPriorityMenuOpen(isOpen => !isOpen)}
      >
        <MetadataButtonLabel>{TASK_PRIORITY_LABELS[priority]}</MetadataButtonLabel>
      </MetadataButton>
      {priorityMenu.content}
    </ControlFrame>
  );
};

const TaskDueDateControl: FC<{
  dueDate?: string;
  onChange: (dueDate: string | undefined) => void;
}> = ({ dueDate, onChange }) => {
  const [isDateMenuOpen, setIsDateMenuOpen] = useState(false);
  const [draftDate, setDraftDate] = useState<DateValue | null>(() => parseTaskDueDate(dueDate));

  useEffect(() => {
    if (!isDateMenuOpen) {
      setDraftDate(parseTaskDueDate(dueDate));
    }
  }, [dueDate, isDateMenuOpen]);

  const updateMenuOpen = (isOpen: boolean) => {
    setIsDateMenuOpen(isOpen);
    if (isOpen) {
      setDraftDate(parseTaskDueDate(dueDate));
    }
  };

  const cancel = () => {
    setDraftDate(parseTaskDueDate(dueDate));
    setIsDateMenuOpen(false);
  };

  const save = () => {
    onChange(draftDate?.toString());
    setIsDateMenuOpen(false);
  };

  const pickerMenu = useTaskFloatingPopover({
    isOpen: isDateMenuOpen,
    onOpenChange: updateMenuOpen,
    children: (
      <DatePickerMenu>
        <MDSCalendar
          ariaLabel="Task due date"
          value={draftDate ?? undefined}
          onChange={setDraftDate}
          weekdayStyle="short"
        />
        <DatePickerActions>
          <MDSButton
            size={MDSButtonSize.Small}
            variant={MDSButtonVariant.Transparent}
            onPress={cancel}
          >
            Cancel
          </MDSButton>
          <MDSButton
            size={MDSButtonSize.Small}
            variant={MDSButtonVariant.Emphasis}
            isDisabled={!draftDate}
            onPress={save}
          >
            Save
          </MDSButton>
        </DatePickerActions>
      </DatePickerMenu>
    ),
  });

  if (!dueDate) {
    return (
      <ControlFrame
        className={TASK_HOVER_CONTROL_CLASS}
        data-menu-open={isDateMenuOpen ? "true" : undefined}
      >
        <MDSIconButton
          ref={pickerMenu.setReference}
          size={MDSIconButtonSize.XSmall}
          iconKind={MDSIconKind.Calendar}
          variant={MDSIconButtonVariant.OnChrome}
          aria-label="Set due date"
          onPress={() => updateMenuOpen(!isDateMenuOpen)}
          rounded
        />
        {pickerMenu.content}
      </ControlFrame>
    );
  }

  return (
    <ControlFrame>
      <MetadataButton
        ref={pickerMenu.setReference}
        type="button"
        aria-label={`Due ${formatDueDate(dueDate)}`}
        onPress={() => updateMenuOpen(!isDateMenuOpen)}
      >
        <MDSIcon kind={MDSIconKind.Calendar} />
        <MetadataButtonLabel>{formatDueDate(dueDate)}</MetadataButtonLabel>
      </MetadataButton>
      {pickerMenu.content}
    </ControlFrame>
  );
};

const TaskLabelInput: FC<{
  task: Task;
  isReadOnly: boolean;
  onChange: (label: string) => void;
  onBlur: () => void;
  onBackspaceEmpty: () => void;
  onEnter: () => void;
}> = ({ task, isReadOnly, onChange, onBlur, onBackspaceEmpty, onEnter }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const showRichLabel = !isEditing && hasChips(task.label);
  if (isReadOnly || showRichLabel) {
    return (
      <LabelViewer
        $done={task.done}
        $clickable={!isReadOnly}
        onClick={isReadOnly ? undefined : () => setIsEditing(true)}
      >
        {task.label.length > 0 ? (
          <ChippyText text={task.label} inline />
        ) : (
          <LabelPlaceholder>New task</LabelPlaceholder>
        )}
      </LabelViewer>
    );
  }

  return (
    <LabelInput
      ref={inputRef}
      $done={task.done}
      value={task.label}
      placeholder="New task"
      readOnly={isReadOnly}
      onChange={e => onChange(e.target.value)}
      onBlur={() => {
        onBlur();
        setIsEditing(false);
      }}
      onKeyDown={e => {
        if (isReadOnly) return;
        if (e.key === "Backspace" && task.label === "") {
          e.preventDefault();
          onBackspaceEmpty();
        } else if (e.key === "Enter") {
          e.preventDefault();
          onEnter();
        }
      }}
    />
  );
};

const TaskRowMenu: FC<{
  hasPriority: boolean;
  hasDueDate: boolean;
  onClearPriority: () => void;
  onClearDueDate: () => void;
  onHide: () => void;
}> = ({ hasPriority, hasDueDate, onClearPriority, onClearDueDate, onHide }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <ControlFrame
      className={TASK_HOVER_CONTROL_CLASS}
      data-menu-open={isMenuOpen ? "true" : undefined}
    >
      <MDSMenuDropdown isOpen={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <MDSIconButton
          slot={MDSMenuDropdownSlotName.Trigger}
          size={MDSIconButtonSize.XSmall}
          iconKind={MDSIconKind.More}
          variant={MDSIconButtonVariant.OnChrome}
          aria-label="Task actions"
          rounded
        />
        <MDSActionMenu aria-label="Task actions" slot={MDSMenuDropdownSlotName.Menu}>
          {hasPriority && (
            <MDSActionMenuItem
              id="clear-priority"
              variant={MDSMenuItemVariant.OneLine}
              label="Clear priority"
              icon={MDSIconKind.Clear}
              onClick={onClearPriority}
            />
          )}
          {hasDueDate && (
            <MDSActionMenuItem
              id="clear-due-date"
              variant={MDSMenuItemVariant.OneLine}
              label="Clear due date"
              icon={MDSIconKind.CalendarXmark}
              onClick={onClearDueDate}
            />
          )}
          <MDSActionMenuItem
            id="hide-task"
            variant={MDSMenuItemVariant.OneLine}
            label="Hide task"
            icon={MDSIconKind.EyeSlash}
            onClick={onHide}
          />
        </MDSActionMenu>
      </MDSMenuDropdown>
    </ControlFrame>
  );
};

const useTaskFloatingPopover = ({
  isOpen,
  onOpenChange,
  children,
}: {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  children: ReactNode;
}) => {
  const { x, y, refs, strategy, context } = useFloating({
    open: isOpen,
    onOpenChange,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });
  const { getFloatingProps } = useInteractions([dismiss, role]);
  const setReference = useCallback(
    (element: HTMLButtonElement | null) => {
      refs.setReference(element);
    },
    [refs]
  );
  const isPositioned = x !== null && y !== null;

  return {
    setReference,
    content: isOpen ? (
      <FloatingPortal>
        <TaskPopoverSurface
          ref={refs.setFloating}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            visibility: isPositioned ? "visible" : "hidden",
          }}
          {...getFloatingProps()}
        >
          {children}
        </TaskPopoverSurface>
      </FloatingPortal>
    ) : null,
  };
};

const buildTaskListContent = (tasks: Task[], sections: WorkflowSection[]) => ({
  kind: "taskList" as const,
  tasks,
  ...(sections.length > 0 ? { sections } : {}),
});

const buildTaskGroups = ({
  tasks,
  sections,
}: {
  tasks: Task[];
  sections: WorkflowSection[];
}): TaskGroup[] => {
  const sectionIds = new Set(sections.map(section => section.id));
  const visibleTasks = tasks.filter(task => !task.hidden);
  const sectionGroups = sections.map(section => ({
    section,
    tasks: visibleTasks.filter(task => task.sectionId === section.id),
    showHeader: true,
  }));
  const unsectionedTasks = visibleTasks.filter(
    task => !task.sectionId || !sectionIds.has(task.sectionId)
  );

  if (sections.length === 0) {
    return [
      {
        section: null,
        tasks: unsectionedTasks,
        showHeader: false,
      },
    ];
  }

  return [
    ...sectionGroups,
    ...(unsectionedTasks.length > 0
      ? [
          {
            section: null,
            tasks: unsectionedTasks,
            showHeader: true,
          },
        ]
      : []),
  ];
};

const getDropPlacement = (event: DragEvent<HTMLElement>): DropPlacement => {
  const rect = event.currentTarget.getBoundingClientRect();
  return event.clientY > rect.top + rect.height / 2 ? "after" : "before";
};

const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: "Low",
  medium: "Med",
  high: "High",
  urgent: "Urgent",
};

const TASK_PRIORITY_OPTIONS: TaskPriority[] = ["low", "medium", "high", "urgent"];

const formatDueDate = (value: string): string => {
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${month}/${day}`;
};

const parseTaskDueDate = (value?: string): DateValue | null => {
  if (!value) return null;
  try {
    return parseCalendarDate(value);
  } catch {
    return null;
  }
};

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
`;

const SectionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  min-height: 24px;
  padding: 2px 0;
`;

const SectionTitle = styled.div`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
`;

const SectionTitleInput = styled.input`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font: inherit;
  font-size: 10px;
  font-weight: 700;
  outline: none;
  padding: 0;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  width: 100%;
`;

const Row = styled.div<{ $dropPlacement: DropPlacement | null }>`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding: 4px 0;
  min-width: 0;
  box-shadow: ${({ $dropPlacement, theme }) => {
    if ($dropPlacement === "before") {
      return `inset 0 2px 0 ${theme.colors.border.emphasis}`;
    }
    if ($dropPlacement === "after") {
      return `inset 0 -2px 0 ${theme.colors.border.emphasis}`;
    }
    return "none";
  }};

  .${TASK_HOVER_CONTROL_CLASS} {
    display: none;
  }

  .${TASK_HOVER_CONTROL_CLASS}[data-menu-open="true"] {
    display: inline-flex;
  }

  &:hover .${TASK_HOVER_CONTROL_CLASS}, &:focus-within .${TASK_HOVER_CONTROL_CLASS} {
    display: inline-flex;
  }
`;

const TaskRowControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex: 0 0 auto;
`;

const TaskDragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  cursor: grab;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }
`;

const InlineSources = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  flex: 0 1 auto;
  gap: 4px;
  max-width: min(220px, 45%);
  min-width: 0;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  flex-shrink: 0;
`;

const LabelInput = styled.input<{ $done: boolean }>`
  border: none;
  background: transparent;
  font-size: 14px;
  color: ${({ $done, theme }) => ($done ? theme.colors.text.tertiary : "inherit")};
  text-decoration: ${({ $done }) => ($done ? "line-through" : "none")};
  outline: none;
  flex: 1;
  font-family: inherit;
  padding: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LabelViewer = styled.div<{ $done: boolean; $clickable: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  flex: 1 1 160px;
  font-size: 14px;
  min-width: 120px;
  color: ${({ $done, theme }) => ($done ? theme.colors.text.tertiary : "inherit")};
  text-decoration: ${({ $done }) => ($done ? "line-through" : "none")};
  cursor: ${({ $clickable }) => ($clickable ? "text" : "default")};
`;

const TaskMetadata = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
`;

const ControlFrame = styled.span`
  display: inline-flex;
  position: relative;
  align-items: center;
  flex: 0 0 auto;
`;

const MetadataButton = styled(Button)<{ $tone?: TaskPriority }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  min-width: 0;
  padding: 0 7px;
  border: 1px solid
    ${({ $tone, theme }) =>
      $tone === "urgent" || $tone === "high"
        ? theme.colors.border.emphasis
        : theme.colors.border.default};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  color: ${({ $tone, theme }) =>
    $tone === "urgent" || $tone === "high"
      ? theme.colors.text.danger
      : theme.colors.text.secondary};
  cursor: pointer;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
    color: ${({ theme }) => theme.colors.text.primary};
    outline: none;
  }
`;

const MetadataButtonLabel = styled.span`
  white-space: nowrap;
`;

const TaskPopoverSurface = styled.div`
  padding-block: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.surface.menu};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  max-width: 90vw;
  max-height: inherit;
  overflow-y: auto;
  scrollbar-width: none;
  z-index: ${ZIndex.ReactAriaPopover};

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
`;

const DatePickerMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
  padding: 0 ${({ theme }) => theme.spacing["3"]};
`;

const DatePickerActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const PriorityBadge = styled.span<{ $priority: TaskPriority }>`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  color: ${({ $priority, theme }) =>
    $priority === "urgent" || $priority === "high"
      ? theme.colors.text.danger
      : theme.colors.text.secondary};
  font-size: 11px;
  font-weight: 600;
  padding: 2px 5px;
`;

const DueDateBadge = styled.span`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 11px;
  padding: 2px 5px;
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["3"]};
`;

const AddRow = styled.button`
  background: transparent;
  border: none;
  text-align: left;
  padding: 4px 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 13px;
  font-family: inherit;
  align-self: flex-start;
  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const LabelPlaceholder = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
`;
